import ChatMessage, {
  TChatMessageProps,
} from "@/components/ChatMessage/ChatMessage";
import { InlineIcon } from "@iconify/react";
import { HighlightOffRounded } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import "./HistoriqueConversation.style.scss";
import { apiGetConversationCursor } from "@/features/profile/api/exchange.api";
import { Toastify } from "@/utils/toast";
import * as moment from "moment";
import "moment/locale/fr";
import {
  TChatConversation,
  TMemberInfos,
  TCursorResult,
} from "@/features/profile/types";
import useExchangeRole from "@/hooks/useExchangeRole";
import NoData from "@/components/No-data/NoData";
import { TMemberProfile } from "@/features/auth/types";
import {
  selectMemberInfos,
  selectMemberProfile,
} from "@/store/reducers/member/member.selector";
import { useSelector } from "react-redux";

interface HistoriqueConversationProps {
  closeModal: () => void;
  conversation_id: string;
}
const HistoriqueConversation = ({
  closeModal,
  conversation_id,
}: HistoriqueConversationProps) => {
  const memberInfosFromStore = useSelector(selectMemberInfos) as TMemberInfos;
  const [discussion, setDiscussion] = useState<TChatMessageProps[]>([]);

  const [disableScrollToBottom, setDisableScrollToBottom] = useState(false);

  const LIMIT_MESSAGE = 50;

  const [loading, setLoading] = useState(false);

  // ========================================
  // NOUVEAUX ÉTATS POUR LE SYSTÈME DE CURSEUR
  // ========================================
  const [cursorResult, setCursorResult] = useState<TCursorResult | null>(null);

  const scrollRef = useRef<any>();
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadConversationByCursor();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollToBottom = useCallback(() => {
    if (scrollRef?.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight + 100;
    }
  }, [scrollRef]);

  // ========================================
  // NOUVELLE FONCTION DE CHARGEMENT PAR CURSEUR
  // ========================================

  /**
   * Charge les messages précédents en utilisant un curseur basé sur la date
   * @param cursorDate - Date ISO string du message le plus ancien (optionnel pour le premier chargement)
   */
  const loadConversationByCursor = useCallback(
    (cursorDate?: string) => {
      // Guard: vérifier qu'on a encore des messages à charger
      if (cursorResult?.hasNextPage === false) {
        console.log(
          "🔄 Curseur HistoriqueConversation: Plus de messages à charger"
        );
        return;
      }

      // Sauvegarder la hauteur de scroll avant chargement
      const container = scrollRef.current;
      const prevScrollHeight = container ? container.scrollHeight : 0;

      setLoading(true);
      console.log(
        "🔄 HistoriqueConversation - Chargement par curseur - Date:",
        cursorDate || "Premier chargement"
      );

      // Appel API avec le curseur (date ISO string)
      apiGetConversationCursor(
        cursorDate || "", // Si pas de curseur, premier chargement
        LIMIT_MESSAGE,
        conversation_id || ""
      )
        .then((response: any) => {
          const data: TChatConversation[] = response.data.result;
          const conversation: TChatMessageProps[] = [...data].map(
            (elt: TChatConversation) => {
              false &&
                console.log(
                  memberInfosFromStore.memberId + " " + elt.sender.memberId
                );
              return {
                _id: elt._id,
                type: (memberInfosFromStore.memberId == elt.sender.memberId
                  ? "sent"
                  : "received") as "sent" | "received",
                gender:
                  elt.sendBy === "STAFF"
                    ? "MALE"
                    : (elt.sender.gender as "MALE" | "FEMALE"),
                fromStaff: elt.sendBy === "STAFF",
                fromAdmin: elt.isGlobal,
                pending: elt.status === "PENDING",
                message: elt.body,
                isValid: elt.status === "VALIDATED",
                reason: elt?.rejectedReason || "",
                note: elt?.moderationNote || "",
                isRead: elt.isRead,
                issueDate: elt.issueDate,
                time:
                  moment.default(new Date(elt.issueDate)).format("LL") +
                  " " +
                  moment.default(new Date(elt.issueDate)).format("LT"),
              };
            }
          );

          setCursorResult(response.data.cursor);
          setLoading(false);

          setDiscussion((prevData) => {
            let newDiscussion: TChatMessageProps[];

            if (cursorDate) {
              // Chargement de messages précédents: ajouter en haut
              newDiscussion = [...conversation, ...prevData];

              // Maintenir la position de scroll
              setTimeout(() => {
                if (container) {
                  const newScrollHeight = container.scrollHeight;
                  container.scrollTop = newScrollHeight - prevScrollHeight;
                  console.log(
                    "📐 HistoriqueConversation - Curseur: Position de scroll maintenue"
                  );
                }
              }, 0);
            } else {
              // Premier chargement: remplacer toute la discussion
              newDiscussion = [...conversation];

              // Scroll vers le bas pour le premier chargement
              setTimeout(() => scrollToBottom(), 500);
            }

            return newDiscussion;
          });

          if (cursorDate === undefined) {
            // Double scroll pour s'assurer que ça fonctionne sur tous les devices
            setTimeout(() => scrollToBottom(), 50);
            setTimeout(() => scrollToBottom(), 200);
          }
        })
        .catch((error) => {
          setLoading(false);
          console.log(
            "❌ HistoriqueConversation - Erreur chargement curseur:",
            error
          );
          error?.response?.data?.message &&
            Toastify(`${error?.response?.data?.message}`, "error", 5000);
        });
    },
    [
      cursorResult?.hasNextPage,
      conversation_id,
      memberInfosFromStore.memberId,
      scrollToBottom,
    ]
  );

  /**
   * Obtient la date du message le plus ancien dans la liste actuelle
   * @returns Date ISO string du message le plus ancien ou undefined si pas de messages
   */
  const getOldestMessageDate = useCallback((): string | undefined => {
    if (discussion.length === 0) return undefined;

    // Trier par date croissante et prendre le premier (le plus ancien)
    const sortedByDate = [...discussion].sort(
      (a, b) =>
        new Date(a.issueDate || "").getTime() -
        new Date(b.issueDate || "").getTime()
    );

    const oldestDate = sortedByDate[0]?.issueDate || undefined;
    console.log(
      "📅 HistoriqueConversation - Date du message le plus ancien:",
      oldestDate
    );

    return oldestDate;
  }, [discussion]);

  // ========================================
  // MODIFICATION DE LA LOGIQUE DE SCROLL POUR UTILISER LE CURSEUR
  // ========================================
  const handleScroll = useCallback(() => {
    const container = scrollRef.current;
    if (!container || loading) return;

    // Gestion du scroll pour le chargement des messages avec curseur
    if (container.scrollTop === 0) {
      if (cursorResult?.hasNextPage) {
        setDisableScrollToBottom(true);
        const oldestDate = cursorResult?.nextCursor || getOldestMessageDate();
        loadConversationByCursor(oldestDate);
      } else {
        console.log(
          "🔄 HistoriqueConversation - Curseur: Fin de l'historique atteinte"
        );
      }
    }
  }, [
    loading,
    cursorResult?.hasNextPage,
    cursorResult?.nextCursor,
    getOldestMessageDate,
    loadConversationByCursor,
  ]);

  useEffect(() => {
    const container = scrollRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
    };
  }, [handleScroll]);

  useEffect(() => {
    !disableScrollToBottom && scrollToBottom();
  }, [discussion, disableScrollToBottom, scrollToBottom]);

  return (
    <div className="historique-conversation-scroll-container">
      <div className="historique-conversation-card">
        <img src={require("@/assets/images/png/logo-mini.png")} alt="logo" />

        <div className="discussion-container" ref={scrollRef}>
          {loading && (
            <img
              src={require("@/assets/images/gif/loadmore.gif")}
              height="70"
              width="70"
              className="load-more"
              alt="load-more"
            />
          )}
          {!loading && discussion.length === 0 && <NoData />}
          {discussion.length > 0 && (
            <>
              {discussion
                .sort(
                  (a, b) =>
                    new Date(a?.issueDate || "").getTime() -
                    new Date(b?.issueDate || "").getTime()
                )
                .map((msg, index) => {
                  return (
                    <ChatMessage
                      key={index}
                      _id={msg._id}
                      message={msg.message}
                      reason={msg.reason}
                      fromStaff={msg.fromStaff}
                      pending={msg.pending}
                      isValid={msg.isValid}
                      type={msg.type}
                      gender={msg.gender}
                      time={msg.time}
                      isRead={msg.isRead}
                      note={msg.note}
                    />
                  );
                })}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default HistoriqueConversation;
